// --- CONFIGURATION ---

// TIMING SETTINGS (all in seconds for production: 10 minutes each)
const CHALLENGE_PHASE_SECONDS = {
  PROMPT_VOTING: 600,      // 10 minutes in seconds
  SUBMITTING: 600,         // 10 minutes in seconds
  VOTING: 600,             // 10 minutes in seconds
};

import { Challenge, Prompt, Submission, ChallengeStatus, Comment, User, Matchup } from './types';

// Example prompts for challenges
const EXAMPLE_PROMPTS = [
  "Shadow Play",
  "Urban Wilderness",
  "Reflections",
  "Hidden Beauty",
  "Lines and Patterns",
  "Morning Light",
  "Vibrant Colors",
  "Silhouettes",
  "Textures",
  "Negative Space",
  "Street Life",
  "Human Element",
  "Minimalism",
  "Odd One Out",
  "Weather Effects",
  "Architecture",
  "Nature in the City",
  "Contrasts",
  "Depth",
  "Movement",
];

// Generate random prompts from the list
const generateRandomPrompts = (count: number): Prompt[] => {
  const shuffled = [...EXAMPLE_PROMPTS].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count).map((text, index) => ({
    id: `prompt-${Date.now()}-${index}`,
    text,
    votes: 0
  }));
};

// Generate a timestamp for N minutes from now
const getMinutesFromNow = (minutes: number): string => {
  const date = new Date();
  date.setMinutes(date.getMinutes() + minutes);
  return date.toISOString();
};

// NEW: Generate timestamp by offset in seconds
const getSecondsFromNow = (seconds: number): string => {
  const date = new Date();
  date.setSeconds(date.getSeconds() + seconds);
  return date.toISOString();
};

// --- CHALLENGE TIMING CONFIGURATION --- //
// Each challenge phase is now 10 minutes.
// Prompt voting, submission, and voting windows derive from CHALLENGE_PHASE_SECONDS above.
const promptVotingEndTime = getSecondsFromNow(CHALLENGE_PHASE_SECONDS.PROMPT_VOTING);
const submissionDeadline = getSecondsFromNow(CHALLENGE_PHASE_SECONDS.PROMPT_VOTING + CHALLENGE_PHASE_SECONDS.SUBMITTING);
const votingDeadline = getSecondsFromNow(
  CHALLENGE_PHASE_SECONDS.PROMPT_VOTING + CHALLENGE_PHASE_SECONDS.SUBMITTING + CHALLENGE_PHASE_SECONDS.VOTING
);

// We'll keep a "start time" for the round
const ROUND_START = new Date();

// Generate a timestamp for a specific hour today or tomorrow
const getTimestamp = (hour: number, minutes: number = 0, tomorrow: boolean = false): string => {
  const date = new Date();
  if (tomorrow) {
    date.setDate(date.getDate() + 1);
  }
  date.setHours(hour, minutes, 0, 0);
  return date.toISOString();
};

// Mock challenge data
let CURRENT_CHALLENGE: Challenge = {
  id: `challenge-${Date.now()}`,
  date: new Date().toISOString().split('T')[0],
  promptOptions: generateRandomPrompts(3),
  promptVotingEndTime, // Voting for prompts ends in 3 min
  submissions: [],
  submissionDeadline, // Submitting ends 3 min after that (so 6 min after challenge creation)
  votingDeadline, // Voting ends 10 min after that (so 16 min after challenge creation)
  status: 'voting-prompts'
};

// A/B voting: Store per-user matchups and win counts for the current challenge
type MatchupStore = {
  [userId: string]: Matchup[];
};
type WinCountStore = {
  [submissionId: string]: number;
};

let MATCHUPS: MatchupStore = {};
let WIN_COUNTS: WinCountStore = {};

// Helper to shuffle an array
const arrayShuffle = <T,>(arr: T[]): T[] =>
  [...arr].sort(() => Math.random() - 0.5);

// Create matchups for a user (if none exist yet for this challenge)
const getOrCreateMatchups = (userId: string): Matchup[] => {
  if (!MATCHUPS[userId] || MATCHUPS[userId].length === 0) {
    // Only use submissions that haven't been eliminated in this round
    const submissions = [...CURRENT_CHALLENGE.submissions];
    const pairs: [Submission, Submission][] = [];
    const shuffled = arrayShuffle(submissions);

    for (let i = 0; i < shuffled.length - 1; i += 2) {
      pairs.push([shuffled[i], shuffled[i + 1]]);
    }
    // If odd, last one gets a free win
    if (shuffled.length % 2 === 1) {
      pairs.push([shuffled[shuffled.length - 1], shuffled[shuffled.length - 1]]); // self-pair indicates skip
    }

    MATCHUPS[userId] = pairs.map(([left, right], idx) => ({
      id: `matchup-${Date.now()}-${userId}-${idx}`,
      left,
      right,
      userId,
      completed: false,
    }));
  }
  return MATCHUPS[userId];
};

// Get the next active matchup for the user, or null if all done
export const getNextMatchup = async (userId: string): Promise<Matchup | null> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const matchups = getOrCreateMatchups(userId);
      const next = matchups.find(m => !m.completed && m.left.id !== m.right.id);
      resolve(next ?? null);
    }, 200);
  });
};

// Handle a user's vote for a matchup: record winner, increment win count
export const voteForMatchup = async (
  userId: string,
  matchupId: string,
  winnerId: string
): Promise<{ winnerId: string; nextMatchup: Matchup | null }> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const matchups = MATCHUPS[userId] || [];
      const matchup = matchups.find(m => m.id === matchupId);
      if (matchup && !matchup.completed) {
        matchup.completed = true;
        matchup.winnerId = winnerId;
        // Increment win count
        if (!WIN_COUNTS[winnerId]) WIN_COUNTS[winnerId] = 0;
        WIN_COUNTS[winnerId]++;
      }
      // Get next matchup for user
      const next = matchups.find(m => !m.completed && m.left.id !== m.right.id);
      resolve({ winnerId, nextMatchup: next ?? null });
    }, 200);
  });
};

// Reset all matchups/win counts at new voting start or new challenge
export const resetABVoting = () => {
  MATCHUPS = {};
  WIN_COUNTS = {};
};

// Winner determination logic: Most wins after round(s)
const determineWinner = () => {
  if (Object.keys(WIN_COUNTS).length === 0) return undefined;
  let highest = 0, winnerId = '';
  for (const [subId, count] of Object.entries(WIN_COUNTS)) {
    if (count > highest) {
      highest = count;
      winnerId = subId;
    }
  }
  const winner = CURRENT_CHALLENGE.submissions.find(s => s.id === winnerId);
  return winner && highest > 0 ? winner : undefined;
};

// Update challenge status (add A/B voting logic)
const updateChallengeStatus = (): ChallengeStatus => {
  const now = new Date();
  const promptVotingEnd = new Date(CURRENT_CHALLENGE.promptVotingEndTime);
  const submissionDeadline = new Date(CURRENT_CHALLENGE.submissionDeadline);
  const votingDeadline = new Date(CURRENT_CHALLENGE.votingDeadline);

  if (now < promptVotingEnd) {
    return 'voting-prompts';
  } else if (now < submissionDeadline) {
    // Select the winning prompt if not already selected
    if (!CURRENT_CHALLENGE.selectedPrompt) {
      const winningPrompt = [...CURRENT_CHALLENGE.promptOptions].sort((a, b) => b.votes - a.votes)[0];
      CURRENT_CHALLENGE.selectedPrompt = winningPrompt;
    }
    return 'submitting';
  } else if (now < votingDeadline) {
    return 'voting-submissions'; // A/B voting UI phase
  } else {
    // NEW: Determine winner by A/B logic
    if (CURRENT_CHALLENGE.submissions.length > 0) {
      CURRENT_CHALLENGE.winner = determineWinner();
    }
    return 'closed';
  }
};

// --- Mock Submission Seeding (for testing) ---
const TEST_USERNAMES = [
  "photogeek", "snapmaster", "catmom", "beastmode", "mountainman",
  "urbanshooter", "vividview", "naturekid", "patternist", "earlybird",
  "colourcraze", "shadowchaser", "citywanderer", "reflex", "streetwise"
];
const PLACEHOLDER_IMAGES = [
  "https://images.unsplash.com/photo-1649972904349-6e44c42644a7",
  "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b",
  "https://images.unsplash.com/photo-1518770660439-4636190af475",
  "https://images.unsplash.com/photo-1461749280684-dccba630e2f6",
  "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158",
  "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5",
  "https://images.unsplash.com/photo-1531297484001-80022131f5a1",
  "https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7",
  "https://images.unsplash.com/photo-1605810230434-7631ac76ec81",
  "https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b",
  "https://images.unsplash.com/photo-1498050108023-c5249f4df085",
  "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952",
  "https://images.unsplash.com/photo-1472396961693-142e6e269027",
  "https://images.unsplash.com/photo-1433086966358-54859d0ed716",
  "https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07"
];

function seedTestSubmissionsIfNeeded() {
  if (CURRENT_CHALLENGE.submissions.length === 0) {
    for (let i = 0; i < 12; i++) {
      CURRENT_CHALLENGE.submissions.push({
        id: `submission-test-${i}`,
        userId: String(100 + i),
        username: TEST_USERNAMES[i % TEST_USERNAMES.length],
        photoUrl: PLACEHOLDER_IMAGES[i % PLACEHOLDER_IMAGES.length],
        votes: Math.floor(Math.random() * 20),
        timestamp: new Date(Date.now() - i * 1000 * 60).toISOString(),
        comments: [],
        likedBy: []
      });
    }
  }
}

// API functions
export const getChallenge = async (): Promise<Challenge> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Seed test submissions if there are none
      seedTestSubmissionsIfNeeded();

      // Update status based on current time
      CURRENT_CHALLENGE.status = updateChallengeStatus();
      resolve(CURRENT_CHALLENGE);
    }, 500);
  });
};

export const voteForPrompt = async (promptId: string, userId: string): Promise<Challenge> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Find the prompt and increment its votes
      CURRENT_CHALLENGE.promptOptions = CURRENT_CHALLENGE.promptOptions.map(prompt => {
        if (prompt.id === promptId) {
          return { ...prompt, votes: prompt.votes + 1 };
        }
        return prompt;
      });
      
      // Update status
      CURRENT_CHALLENGE.status = updateChallengeStatus();
      resolve(CURRENT_CHALLENGE);
    }, 500);
  });
};

export const submitPhoto = async (userId: string, username: string, photoUrl: string): Promise<Challenge> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Add the submission
      const newSubmission: Submission = {
        id: `submission-${Date.now()}-${userId}`,
        userId,
        username,
        photoUrl,
        votes: 0,
        timestamp: new Date().toISOString()
      };
      
      CURRENT_CHALLENGE.submissions.push(newSubmission);
      
      // Update status
      CURRENT_CHALLENGE.status = updateChallengeStatus();
      resolve(CURRENT_CHALLENGE);
    }, 500);
  });
};

export const voteForSubmission = async (submissionId: string, userId: string): Promise<Challenge> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Find the submission
      const submission = CURRENT_CHALLENGE.submissions.find(s => s.id === submissionId);
      
      if (submission) {
        // Initialize likedBy array if it doesn't exist
        if (!submission.likedBy) {
          submission.likedBy = [];
        }
        
        // Check if user already liked this submission
        if (!submission.likedBy.includes(userId)) {
          // Add user to likedBy array and increment votes
          submission.likedBy.push(userId);
          submission.votes += 1;
        }
      }
      
      // Update status
      CURRENT_CHALLENGE.status = updateChallengeStatus();
      resolve(CURRENT_CHALLENGE);
    }, 500);
  });
};

export const addComment = async (submissionId: string, userId: string, username: string, text: string, photoUrl?: string): Promise<Challenge> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Find the submission
      const submission = CURRENT_CHALLENGE.submissions.find(s => s.id === submissionId);
      
      if (submission) {
        // Initialize comments array if it doesn't exist
        if (!submission.comments) {
          submission.comments = [];
        }
        
        // Create new comment
        const newComment: Comment = {
          id: `comment-${Date.now()}-${userId}`,
          userId,
          username,
          photoUrl,
          text,
          timestamp: new Date().toISOString()
        };
        
        // Add comment to submission
        submission.comments.push(newComment);
      }
      
      resolve(CURRENT_CHALLENGE);
    }, 500);
  });
};

// Mock users database (expanded from auth.ts)
export const MOCK_USERS: User[] = [
  {
    id: '1',
    username: 'photogeek',
    email: 'photo@example.com',
    points: 120,
    photoUrl: 'https://i.pravatar.cc/150?img=1',
    streakDays: 7,
    followers: ['2'],
    following: []
  },
  {
    id: '2',
    username: 'snapmaster',
    email: 'snap@example.com',
    points: 85,
    photoUrl: 'https://i.pravatar.cc/150?img=2',
    streakDays: 3,
    followers: [],
    following: ['1']
  }
];

// Follow/unfollow a user
export const toggleFollowUser = async (currentUserId: string, targetUserId: string): Promise<User> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // Find both users
      const currentUser = MOCK_USERS.find(u => u.id === currentUserId);
      const targetUser = MOCK_USERS.find(u => u.id === targetUserId);
      
      if (currentUser && targetUser) {
        // Initialize arrays if they don't exist
        if (!currentUser.following) currentUser.following = [];
        if (!targetUser.followers) targetUser.followers = [];
        
        // Check if already following
        const isFollowing = currentUser.following.includes(targetUserId);
        
        if (isFollowing) {
          // Unfollow: remove from arrays
          currentUser.following = currentUser.following.filter(id => id !== targetUserId);
          targetUser.followers = targetUser.followers.filter(id => id !== currentUserId);
        } else {
          // Follow: add to arrays
          currentUser.following.push(targetUserId);
          targetUser.followers.push(currentUserId);
        }
        
        resolve(currentUser);
      } else {
        throw new Error('User not found');
      }
    }, 500);
  });
};

// Mock function to simulate camera capture
export const capturePhoto = async (file?: File): Promise<string> => {
  return new Promise((resolve) => {
    // If a file is provided, use it instead of a random image
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          resolve(e.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    } else {
      // Fallback to the random image for the camera simulation
      const randomId = Math.floor(Math.random() * 1000);
      const randomImage = `https://source.unsplash.com/random/500x500?sig=${randomId}`;
      
      setTimeout(() => {
        resolve(randomImage);
      }, 1000);
    }
  });
};
